import { useEffect, useState, type FormEvent } from "react";
import {
  createFormGrant,
  deleteFormGrant,
  fetchFormGrants,
  updateFormGrant,
} from "@/api/formGrants";
import { ApiError } from "@/api/client";
import { IdentityUserPicker } from "@/components/admin/IdentityUserPicker";
import { FieldLabel, SelectInput } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { formGrantRoleLabel } from "@/lib/forms/formGrants";
import type { FormGrantRole, FormUserGrantDto } from "@/types/formGrant";
import type { IdentityUser } from "@/types/identityUser";

const ROLE_OPTIONS: FormGrantRole[] = ["manage", "view"];

export function FormGrantsSettings({ formId }: { formId: number }) {
  const [grants, setGrants] = useState<FormUserGrantDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<IdentityUser | null>(null);
  const [role, setRole] = useState<FormGrantRole>("view");
  const [adding, setAdding] = useState(false);
  const [busyGrantId, setBusyGrantId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setForbidden(false);
    setError(null);

    void fetchFormGrants(formId)
      .then((items) => {
        if (cancelled) return;
        setGrants(items);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setForbidden(true);
          return;
        }
        setError(
          err instanceof ApiError
            ? err.message
            : "Не удалось загрузить персональный доступ",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formId]);

  if (forbidden) return null;

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault();
    if (!user || adding) return;

    setAdding(true);
    setError(null);
    try {
      const grant = await createFormGrant(formId, { chmId: user.id, role });
      setGrants((current) => [...current, grant]);
      setUser(null);
      setRole("view");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Не удалось выдать доступ",
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (grant: FormUserGrantDto, nextRole: FormGrantRole) => {
    if (nextRole === grant.role || busyGrantId != null) return;
    setBusyGrantId(grant.id);
    setError(null);
    try {
      const updated = await updateFormGrant(formId, grant.id, { role: nextRole });
      setGrants((current) =>
        current.map((item) => (item.id === grant.id ? updated : item)),
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось сменить роль",
      );
    } finally {
      setBusyGrantId(null);
    }
  };

  const handleRemove = async (grant: FormUserGrantDto) => {
    if (busyGrantId != null) return;
    setBusyGrantId(grant.id);
    setError(null);
    try {
      await deleteFormGrant(formId, grant.id);
      setGrants((current) => current.filter((item) => item.id !== grant.id));
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось отозвать доступ",
      );
    } finally {
      setBusyGrantId(null);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-card-lg sm:p-5">
      <h3 className="mb-1 font-onest text-sm font-semibold text-ink">
        Персональный доступ
      </h3>
      <p className="mb-4 font-onest text-xs text-slate-500">
        Найдите пользователя CHM.ID по почте, телефону или идентификатору.
        Приглашать в организацию не нужно.
      </p>

      <form
        onSubmit={(event) => void handleAdd(event)}
        className="mb-4 rounded-xl border border-slate-200/80 bg-slate-50/60 p-3"
      >
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem_auto] sm:items-end">
          <IdentityUserPicker
            value={user}
            onChange={setUser}
            disabled={adding}
          />
          <div>
            <FieldLabel htmlFor="form-grant-role" size="compact">
              Роль
            </FieldLabel>
            <SelectInput
              id="form-grant-role"
              size="compact"
              value={role}
              onChange={(value) => setRole(value as FormGrantRole)}
            >
              {ROLE_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {formGrantRoleLabel(item)}
                </option>
              ))}
            </SelectInput>
          </div>
          <Button
            type="submit"
            className="px-4 py-2 text-sm"
            disabled={adding || !user}
          >
            {adding ? "Добавление…" : "Выдать"}
          </Button>
        </div>
      </form>

      {error ? (
        <p className="mb-3 font-onest text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="font-onest text-sm text-slate-500">Загрузка…</p>
      ) : grants.length === 0 ? (
        <p className="font-onest text-sm text-slate-500">
          Персональный доступ пока не выдан
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200/80">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Пользователь
                </th>
                <th className="px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Роль
                </th>
                <th className="px-3 py-2 text-right font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody>
              {grants.map((grant) => (
                <tr key={grant.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-3 py-2 font-onest text-sm text-slate-800">
                    <div>{grant.name || `CHM ${grant.chmId}`}</div>
                    <div className="text-xs text-slate-500">{grant.chmId}</div>
                    {!grant.inOrganisation && grant.warning ? (
                      <div className="mt-1 inline-flex rounded-md bg-amber-50 px-1.5 py-0.5 font-onest text-[11px] font-medium text-amber-800">
                        {grant.warning}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <SelectInput
                      size="compact"
                      value={grant.role}
                      disabled={busyGrantId != null}
                      onChange={(value) =>
                        void handleRoleChange(grant, value as FormGrantRole)
                      }
                    >
                      {ROLE_OPTIONS.map((item) => (
                        <option key={item} value={item}>
                          {formGrantRoleLabel(item)}
                        </option>
                      ))}
                    </SelectInput>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      disabled={busyGrantId != null}
                      onClick={() => void handleRemove(grant)}
                      className="font-onest text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                    >
                      Отозвать
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
